import { removeNullParams, snakeCaseObject } from '@/helper/formatter'
import { requests, responseBody } from '../http/axios'
import {
  CalendarEvent,
  Contact,
  CreateOrderRequest,
  MasterOrder,
  Order,
  Paginator,
} from '@/types'
import { format } from 'date-fns'
import { pick } from 'lodash-es'

export type OrderFilters = {
  limit?: number
  page?: number
  q?: string
  status?: string
  dateBy?: string
  from?: string
  until?: string
  organizerId?: string
  createdById?: string
  sellerId?: string
  searchBy?: string
  orderIds?: number[] | string
  orderBy?: string
  orderDirection?: string
}

export const OrderService = {
  getOrders: async (params: OrderFilters): Promise<Paginator<Order>> =>
    await requests.get(`orders?` + removeNullParams(params).toString()),

  getOrdersByIds: async (params: OrderFilters): Promise<Paginator<Order>> =>
    await requests.get(
      `get_orders_by_ids?` + removeNullParams(params).toString()
    ),

  getValidatedOrders: async (params: OrderFilters): Promise<Paginator<Order>> =>
    await requests.get(
      `validated_orders?` + removeNullParams(params).toString()
    ),

  getOrder: async (id: string | number): Promise<Order> =>
    await requests.get(`orders/${id}`).then(responseBody),

  getMasterOrder: async (idempotency: string): Promise<MasterOrder> =>
    await requests.get(`master_orders/${idempotency}`).then(responseBody),

  createOrder: async (
    idempotency: string,
    orderRequest: CreateOrderRequest
  ): Promise<MasterOrder> =>
    await requests
      .post(`orders`, {
        ...orderRequest,
        selectedDate: format(orderRequest.selectedDate!, 'yyyy-MM-dd'),
        idempotency,
      })
      .then(responseBody),

  markMasterOrderAsExpired: async (id: string): Promise<void> =>
    await requests.put(`master_orders/${id}/mark_as_expired`, {}),

  removeOrder: async (idempotency: string, id: number): Promise<void> =>
    await requests.delete(`orders/${id}?idempotency=${idempotency}`),

  updateContacts: async (id: string, contact: Contact): Promise<void> =>
    await requests.put(`master_orders/${id}/update_customer`, {
      contacts: [snakeCaseObject(contact)],
    }),

  placeMasterOrder: async (masterOrder: MasterOrder): Promise<MasterOrder> =>
    await requests
      .put(
        `master_orders/${masterOrder.id}/place_order`,
        snakeCaseObject(
          pick(masterOrder, ['locale', 'paymentMethod', 'additionalNotes'])
        )
      )
      .then(responseBody),

  blockBooking: async (params: {
    experienceId: number
    selectedDate: string
    startsSlot: string
    sellerId: number
    optionId: string
  }): Promise<void> =>
    await requests.post(`experiences/${params.experienceId}/block_booking`, {
      selectedDate: params.selectedDate,
      startsSlot: params.startsSlot,
      sellerId: params.sellerId,
      optionId: params.optionId,
    }),

  refresh: async (masterOrder: MasterOrder): Promise<MasterOrder> =>
    await requests.put(`master_orders/${masterOrder.id}/refresh`, {}),

  getOrCreateClientSecret: async (id: string): Promise<string> =>
    await requests.post(`master_orders/${id}/client_secret`, {}),

  markFreeOrderAsPaid: async (id: string): Promise<void> =>
    await requests.post(`master_orders/${id}/complete-free-order`, {}),

  markAsPaid: async (id: number): Promise<void> =>
    await requests.put(`orders/${id}/mark_as_paid`, {}),

  markAsCanceled: async (id: number): Promise<void> =>
    await requests.put(`orders/${id}/mark_as_canceled`, {}),

  markAsCompleted: async (id: number): Promise<void> =>
    await requests.put(`orders/${id}/mark_as_completed`, {}),

  markAsConfirmed: async (id: number): Promise<void> =>
    await requests.put(`orders/${id}/mark_as_confirmed`, {}),

  validateOrder: async (orderId: string): Promise<Order> =>
    await requests.post(`orders/${orderId}/validate`, {}),

  getCalendarEvents: async (
    startsAt: string,
    endsAt: string
  ): Promise<CalendarEvent[]> =>
    await requests.get(`order_events/${startsAt}/${endsAt}`).then(responseBody),
}
